package com.patronat.top.library.media.utils

import io.github.vinceglb.filekit.PlatformFile
import kotlinx.browser.document
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.Image
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.FileReader
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine
import kotlin.math.max
import kotlin.math.min

actual object ImageCompressor {
    actual suspend fun compressAndCrop(
        file: PlatformFile,
        maxWidth: Int,
        maxHeight: Int,
        quality: Double
    ): ByteArray {
        val jsFile = file.file

        val img = loadImage(jsFile)

        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = maxWidth
        canvas.height = maxHeight
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D

        val sourceWidth = img.naturalWidth.toDouble()
        val sourceHeight = img.naturalHeight.toDouble()
        val targetWidth = maxWidth.toDouble()
        val targetHeight = maxHeight.toDouble()

        val scale = max(targetWidth / sourceWidth, targetHeight / sourceHeight)
        val scaledWidth = sourceWidth * scale
        val scaledHeight = sourceHeight * scale

        val dx = (targetWidth - scaledWidth) / 2
        val dy = (targetHeight - scaledHeight) / 2

        ctx.drawImage(img, dx, dy, scaledWidth, scaledHeight)

        val blob = canvasToBlob(canvas, quality)

        return blobToByteArray(blob)
    }

    actual suspend fun compressToJpeg(
        file: PlatformFile,
        maxWidth: Int,
        maxHeight: Int,
        quality: Double
    ): ByteArray {
        val jsFile = file.file
        val img = loadImage(jsFile)

        val sourceWidth = img.naturalWidth.toDouble()
        val sourceHeight = img.naturalHeight.toDouble()
        if (sourceWidth <= 0.0 || sourceHeight <= 0.0) return ByteArray(0)

        val scale = min(
            1.0,
            min(maxWidth.toDouble() / sourceWidth, maxHeight.toDouble() / sourceHeight)
        )
        val targetWidth = (sourceWidth * scale).toInt().coerceAtLeast(1)
        val targetHeight = (sourceHeight * scale).toInt().coerceAtLeast(1)

        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = targetWidth
        canvas.height = targetHeight
        val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
        ctx.drawImage(img, 0.0, 0.0, targetWidth.toDouble(), targetHeight.toDouble())

        val blob = canvasToBlob(canvas, quality)
        return blobToByteArray(blob)
    }

    private suspend fun loadImage(file: org.w3c.files.File): HTMLImageElement = suspendCoroutine { cont ->
        val url = URL.createObjectURL(file)
        val img = Image()

        img.onload = {
            URL.revokeObjectURL(url)
            cont.resume(img)
        }

        img.onerror = { _, _, _, _, _ ->
            URL.revokeObjectURL(url)
            cont.resumeWithException(Exception("Failed to load image"))
            null
        }

        img.src = url
    }

    private suspend fun canvasToBlob(canvas: HTMLCanvasElement, quality: Double): Blob = suspendCoroutine { cont ->
        canvas.toBlob({ blob ->
            if (blob != null) cont.resume(blob)
            else cont.resumeWithException(Exception("Canvas encoding failed"))
        }, "image/jpeg", quality)
    }

    private suspend fun blobToByteArray(blob: Blob): ByteArray = suspendCoroutine { cont ->
        val reader = FileReader()

        reader.onload = {
            val buffer = reader.result as ArrayBuffer
            val array = Int8Array(buffer)
            cont.resume(array.unsafeCast<ByteArray>())
        }

        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read blob bytes"))
        }

        reader.readAsArrayBuffer(blob)
    }
}
